import React from 'react';
import { AnchorLink, type LinkComponent } from './LinkComponent';
import { BLOG_DESCRIPTION, formatDate } from './seo';
import type { BlogPost } from './types';

/** The /blog landing: title plus a card per post. Pure so the prerender can snapshot it. */
export const PostList: React.FC<{ posts: BlogPost[]; Link?: LinkComponent }> = ({ posts, Link = AnchorLink }) => (
  <div className="bg-bg">
    <div className="max-w-6xl mx-auto px-5 md:px-8 pt-28 md:pt-36 pb-20 md:pb-28">
      <header className="max-w-3xl mb-12 md:mb-16">
        <p className="m-0 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-muted">Blog</p>
        <h1 className="mt-3 font-display font-bold text-[36px] md:text-[52px] leading-[1.06] tracking-[-0.03em] text-ink m-0">
          Cook more, scroll less
        </h1>
        <p className="mt-5 text-[17px] md:text-[19px] leading-relaxed text-ink-muted m-0">{BLOG_DESCRIPTION}</p>
      </header>

      <ul className="m-0 p-0 list-none grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {posts.map((p) => (
          <li key={p.slug} className="flex">
            <Link
              to={`/blog/${p.slug}`}
              className="group flex flex-col w-full no-underline rounded-[24px] bg-surface border-hairline shadow-card overflow-hidden hover:border-[color:var(--hairline-strong)] transition-colors"
            >
              <span className="block aspect-[16/10] bg-surface-alt overflow-hidden">
                <img
                  src={p.coverImage.src}
                  alt={p.coverImage.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </span>
              <span className="flex flex-col flex-1 p-6">
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent-deep">{p.category}</span>
                <span className="mt-2.5 font-display font-bold text-[21px] leading-[1.25] tracking-[-0.02em] text-ink">{p.title}</span>
                <span className="mt-2.5 text-[15px] leading-relaxed text-ink-muted line-clamp-3">{p.description}</span>
                <span className="mt-auto pt-5 text-[13px] text-ink-muted">
                  <time dateTime={p.updatedAt}>{formatDate(p.updatedAt)}</time> · {p.readingMinutes} min read
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default PostList;
